import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SyntaxHighlightInput from './ui/SyntaxHighlightInput';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { SUGGEST_VARIABLES } from '../utils/variableSuggestions';
import {
  COLLECTION_AUTH_TYPES,
  REQUEST_AUTH_TYPES,
  normalizeAuth,
} from '../utils/requestAuth';
import { isTauri } from '../utils/tauri';
import { runOAuthPkceFlow, runOAuthClientCredentials } from '../utils/oauthFlow';
import { createScopedVariableResolver, storedScopesFrom } from '../utils/scopedVariableResolver';

const AUTH_URL_INPUT_CLASS =
  'url-input request-url-bar__url-input border-0 rounded-none focus:outline-none min-h-0 bg-transparent';
const AUTH_URL_INPUT_STYLE = { minHeight: '36px' };

const OAUTH_GRANT_OPTIONS = [
  { value: 'manual', label: 'Paste access token' },
  { value: 'authorization_code_pkce', label: 'Authorization Code + PKCE' },
  { value: 'client_credentials', label: 'Client credentials' },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-zinc-400">{label}</label>
      {children}
    </div>
  );
}

function AuthFieldShell({ children, innerClassName = 'url-container__url min-w-0 w-full flex flex-col justify-center bg-white' }) {
  return (
    <div className="request-url-field-group w-full rounded-lg border border-gray-200 overflow-hidden bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <div className={innerClassName}>{children}</div>
    </div>
  );
}

function AuthSelectField({ label, className = '', ...pickerProps }) {
  return (
    <Field label={label}>
      <AuthFieldShell innerClassName="auth-picker-field-wrap">
        <CompactSelectPicker {...pickerProps} className={`w-full ${className}`.trim()} />
      </AuthFieldShell>
    </Field>
  );
}

function AuthTextField({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  type = 'text',
  variableProps,
}) {
  const inputProps = {
    placeholder,
    disabled,
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
  };

  return (
    <Field label={label}>
      <AuthFieldShell>
        {variableProps ? (
          <SyntaxHighlightInput
            {...inputProps}
            type={type}
            // Tokens and keys are exactly what belongs in a variable — but NOT
            // from a masked field: extracting a password-typed value would move
            // a hidden secret into a plainly visible variable, which is the same
            // rule the pick-list picker follows.
            selectionToVariable={type !== 'password'}
            // The picker is on explicitly: it defaults to the KV inputs and these
            // borrow the URL field's class (owner, 23.09.2026). Not on a masked
            // field: the list would render beside a value the user deliberately hid.
            showDynamicVarPicker={type !== 'password'}
            // Only the user's own variables — a token is `{{clientSecret}}`, not
            // `{{$randomInt}}` (owner, 28.09.2026, reversing 23.09). Typing
            // `{{$…}}` by hand still resolves at send time.
            suggest={SUGGEST_VARIABLES}
            className={AUTH_URL_INPUT_CLASS}
            style={AUTH_URL_INPUT_STYLE}
            {...variableProps}
          />
        ) : (
          <input type={type} {...inputProps} className="auth-editor-native-input w-full" />
        )}
      </AuthFieldShell>
    </Field>
  );
}

function maskToken(token) {
  if (!token) return '';
  if (token.length <= 8) return '••••••••';
  return `${token.slice(0, 4)}••••${token.slice(-4)}`;
}

export default function AuthEditorPanel({
  auth: authProp,
  onChange,
  disabled = false,
  showInheritOption = false,
  inheritSourceName = null,
  // Replaces the request-worded inherit hint (a folder page says its own).
  inheritHelp = null,
  variableProps = null,
}) {
  const { t } = useTranslation();
  const auth = normalizeAuth(authProp, { forRequest: showInheritOption });
  const types = showInheritOption ? REQUEST_AUTH_TYPES : COLLECTION_AUTH_TYPES;
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthError, setOauthError] = useState(null);

  const patch = (partial) => onChange?.(partial);

  const grantType = auth.oauth2?.grantType || 'manual';

  const handleGetToken = async () => {
    setOauthError(null);
    setOauthBusy(true);
    try {
      // `{{clientId}}` / `{{clientSecret}}` / `{{tokenUrl}}` resolve against the
      // same scopes these fields suggest from; the stored config keeps the
      // `{{…}}` text. Without it the token endpoint got the braces verbatim.
      const substituteVariables = variableProps
        ? createScopedVariableResolver(storedScopesFrom(variableProps))
        : undefined;
      let tokens;
      if (grantType === 'client_credentials') {
        tokens = await runOAuthClientCredentials(auth.oauth2, { substituteVariables });
      } else {
        tokens = await runOAuthPkceFlow(auth.oauth2, { substituteVariables });
      }
      patch({
        oauth2: {
          ...auth.oauth2,
          ...tokens,
          grantType,
        },
      });
    } catch (err) {
      if (err.message === 'OAUTH_DESKTOP_ONLY') {
        setOauthError(t('auth.oauth.desktopOnly'));
      } else {
        setOauthError(err.message || t('auth.oauth.failed'));
      }
    } finally {
      setOauthBusy(false);
    }
  };

  const clearTokens = () => {
    patch({
      oauth2: {
        ...auth.oauth2,
        accessToken: '',
        refreshToken: '',
        expiresAt: null,
      },
    });
  };

  const helperText = showInheritOption ? (
    <p className="text-xs text-gray-500 dark:text-zinc-500">
      {auth.type === 'inherit' ? (
        inheritHelp || (inheritSourceName ? (
          <>
            This request inherits auth from collection <strong>{inheritSourceName}</strong> when
            sent. Choose another type to override.
          </>
        ) : (
          <>
            This request inherits auth from its parent collection when sent. Assign the request to
            a collection or choose another type.
          </>
        ))
      ) : auth.type === 'oauth2' ? (
        grantType === 'manual' ? (
          t('auth.oauth.manualHint')
        ) : (
          t('auth.oauth.pkceHint')
        )
      ) : null}
    </p>
  ) : (
    <p className="text-xs text-gray-500 dark:text-zinc-500">
      Requests default to <strong>Inherit from parent</strong> and use this collection&apos;s auth
      when inherited.
    </p>
  );

  const expiryLabel =
    auth.oauth2?.expiresAt && Number(auth.oauth2.expiresAt) > Date.now()
      ? t('auth.oauth.expires', {
          time: new Date(Number(auth.oauth2.expiresAt)).toLocaleString(),
        })
      : auth.oauth2?.expiresAt
        ? t('auth.oauth.expired')
        : null;

  return (
    <div className="space-y-4 max-w-lg">
      <AuthSelectField
        label="Type"
        value={auth.type || (showInheritOption ? 'inherit' : 'noauth')}
        disabled={disabled}
        onChange={(next) => patch({ type: next })}
        options={types}
        className="max-w-xs"
        aria-label="Authorization type"
      />

      {auth.type === 'awssigv4' && (
        <>
          <AuthTextField
            label="Access Key ID"
            value={auth.awssigv4?.accessKeyId}
            onChange={(v) => patch({ awssigv4: { ...auth.awssigv4, accessKeyId: v } })}
            disabled={disabled}
            placeholder="AKIAIOSFODNN7EXAMPLE"
            variableProps={variableProps}
          />
          <AuthTextField
            label="Secret Access Key"
            value={auth.awssigv4?.secretAccessKey}
            onChange={(v) => patch({ awssigv4: { ...auth.awssigv4, secretAccessKey: v } })}
            disabled={disabled}
            placeholder="Secret access key"
            type="text"
            variableProps={variableProps}
          />
          <AuthTextField
            label="Session Token (optional — for temporary credentials)"
            value={auth.awssigv4?.sessionToken}
            onChange={(v) => patch({ awssigv4: { ...auth.awssigv4, sessionToken: v } })}
            disabled={disabled}
            placeholder="Session token from STS AssumeRole"
            variableProps={variableProps}
          />
          <AuthTextField
            label="AWS Region"
            value={auth.awssigv4?.region}
            onChange={(v) => patch({ awssigv4: { ...auth.awssigv4, region: v } })}
            disabled={disabled}
            placeholder="us-east-1"
            variableProps={variableProps}
          />
          <AuthTextField
            label="Service"
            value={auth.awssigv4?.service}
            onChange={(v) => patch({ awssigv4: { ...auth.awssigv4, service: v } })}
            disabled={disabled}
            placeholder="execute-api, s3, dynamodb, sts…"
            variableProps={variableProps}
          />
        </>
      )}

      {auth.type === 'bearer' && (
        <AuthTextField
          label="Token"
          value={auth.bearer}
          onChange={(v) => patch({ bearer: v })}
          disabled={disabled}
          placeholder="Bearer token"
          variableProps={variableProps}
        />
      )}

      {auth.type === 'apikey' && (
        <>
          <AuthTextField
            label="Key"
            value={auth.apikey?.key}
            onChange={(v) => patch({ apikey: { ...auth.apikey, key: v } })}
            disabled={disabled}
            placeholder="Header or query name"
            variableProps={variableProps}
          />
          <AuthTextField
            label="Value"
            value={auth.apikey?.value}
            onChange={(v) => patch({ apikey: { ...auth.apikey, value: v } })}
            disabled={disabled}
            placeholder="API key value"
            variableProps={variableProps}
          />
          <AuthSelectField
            label="Add to"
            disabled={disabled}
            value={auth.apikey?.in || 'header'}
            onChange={(next) => patch({ apikey: { ...auth.apikey, in: next } })}
            options={[
              { value: 'header', label: 'Header' },
              { value: 'query', label: 'Query params' },
            ]}
            aria-label="Add API key to"
          />
        </>
      )}

      {auth.type === 'basic' && (
        <>
          <AuthTextField
            label="Username"
            value={auth.basic?.username}
            onChange={(v) => patch({ basic: { ...auth.basic, username: v } })}
            disabled={disabled}
            placeholder="Username"
            variableProps={variableProps}
          />
          <AuthTextField
            label="Password"
            value={auth.basic?.password}
            onChange={(v) => patch({ basic: { ...auth.basic, password: v } })}
            disabled={disabled}
            placeholder="Password"
            type="text"
            variableProps={variableProps}
          />
        </>
      )}

      {auth.type === 'oauth2' && (
        <>
          <AuthSelectField
            label={t('auth.oauth.grantType')}
            disabled={disabled}
            value={grantType}
            onChange={(next) => patch({ oauth2: { ...auth.oauth2, grantType: next } })}
            options={OAUTH_GRANT_OPTIONS}
            aria-label={t('auth.oauth.grantType')}
          />

          {grantType === 'authorization_code_pkce' && (
            <>
              <AuthTextField
                label={t('auth.oauth.authUrl')}
                value={auth.oauth2?.authUrl}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, authUrl: v } })}
                disabled={disabled}
                placeholder="https://provider/oauth/authorize"
                variableProps={variableProps}
              />
              <AuthTextField
                label={t('auth.oauth.tokenUrl')}
                value={auth.oauth2?.tokenUrl}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, tokenUrl: v } })}
                disabled={disabled}
                placeholder="https://provider/oauth/token"
                variableProps={variableProps}
              />
              <AuthTextField
                label={t('auth.oauth.clientId')}
                value={auth.oauth2?.clientId}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, clientId: v } })}
                disabled={disabled}
                placeholder="Client ID"
                variableProps={variableProps}
              />
              <AuthTextField
                label={t('auth.oauth.clientSecret')}
                value={auth.oauth2?.clientSecret}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, clientSecret: v } })}
                disabled={disabled}
                placeholder={t('auth.oauth.clientSecretOptional')}
                type="text"
              />
              <AuthTextField
                label={t('auth.oauth.scope')}
                value={auth.oauth2?.scope}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, scope: v } })}
                disabled={disabled}
                placeholder="openid profile email"
                variableProps={variableProps}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={disabled || oauthBusy || !auth.oauth2?.authUrl || !auth.oauth2?.tokenUrl || !auth.oauth2?.clientId}
                  onClick={handleGetToken}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {oauthBusy ? t('auth.oauth.gettingToken') : t('auth.oauth.getToken')}
                </button>
                <button
                  type="button"
                  disabled={disabled || oauthBusy}
                  onClick={clearTokens}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  {t('auth.oauth.clearToken')}
                </button>
              </div>
              {oauthError && <p className="text-xs text-red-600">{oauthError}</p>}
              {!isTauri() && (
                <p className="text-xs text-amber-700 dark:text-amber-300">{t('auth.oauth.desktopOnly')}</p>
              )}
            </>
          )}

          {grantType === 'client_credentials' && (
            <>
              <AuthTextField
                label={t('auth.oauth.tokenUrl')}
                value={auth.oauth2?.tokenUrl}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, tokenUrl: v } })}
                disabled={disabled}
                placeholder="https://provider/oauth/token"
                variableProps={variableProps}
              />
              <AuthTextField
                label={t('auth.oauth.clientId')}
                value={auth.oauth2?.clientId}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, clientId: v } })}
                disabled={disabled}
                placeholder="Client ID"
                variableProps={variableProps}
              />
              <AuthTextField
                label={t('auth.oauth.clientSecret')}
                value={auth.oauth2?.clientSecret}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, clientSecret: v } })}
                disabled={disabled}
                placeholder="Client secret"
                type="text"
              />
              <AuthTextField
                label={t('auth.oauth.scope')}
                value={auth.oauth2?.scope}
                onChange={(v) => patch({ oauth2: { ...auth.oauth2, scope: v } })}
                disabled={disabled}
                placeholder="api:read api:write"
                variableProps={variableProps}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={disabled || oauthBusy || !auth.oauth2?.tokenUrl || !auth.oauth2?.clientId}
                  onClick={handleGetToken}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {oauthBusy ? t('auth.oauth.gettingToken') : t('auth.oauth.getToken')}
                </button>
                <button
                  type="button"
                  disabled={disabled || oauthBusy}
                  onClick={clearTokens}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  {t('auth.oauth.clearToken')}
                </button>
              </div>
              {oauthError && <p className="text-xs text-red-600">{oauthError}</p>}
            </>
          )}

          <Field label="Token type">
            <AuthFieldShell>
              <input
                type="text"
                disabled={disabled}
                value={auth.oauth2?.tokenType || 'Bearer'}
                onChange={(e) => patch({ oauth2: { ...auth.oauth2, tokenType: e.target.value } })}
                className="auth-editor-native-input w-full"
                placeholder="Bearer"
              />
            </AuthFieldShell>
          </Field>

          {grantType === 'manual' ? (
            <AuthTextField
              label="Access token"
              value={auth.oauth2?.accessToken}
              onChange={(v) => patch({ oauth2: { ...auth.oauth2, accessToken: v } })}
              disabled={disabled}
              placeholder="Access token"
              variableProps={variableProps}
            />
          ) : (
            <Field label={t('auth.oauth.accessToken')}>
              <p className="text-xs text-gray-600 font-mono dark:text-zinc-400">
                {auth.oauth2?.accessToken ? maskToken(auth.oauth2.accessToken) : t('auth.oauth.noToken')}
              </p>
              {expiryLabel && (
                <p className="text-xs text-gray-500 mt-1 dark:text-zinc-500">{expiryLabel}</p>
              )}
            </Field>
          )}
        </>
      )}

      {helperText}
    </div>
  );
}
